import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { useAuth } from '../../../context/AuthContext';
import {
  RefreshCw, Eye, Wallet, TrendingDown, Crown, AlertTriangle,
  Presentation, Info, Target, Layers
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

/**
 * Arama Görünürlüğü & Ses Payı — CANLI Google Ads.
 * Route: /ads-platforms/google-ads/visibility
 *
 * Aynı analizi anlatan slayt sunumu /r/asics-gorunurluk adresinde duruyor.
 * Bu sayfa onun panel içindeki, tablo tabanlı ve HER AÇILIŞTA TAZELENEN hâli:
 * sunum 26 Ağustos'ta donmuş bir fotoğraf, burası canlı.
 *
 * 🔑 Marka bağlamı useAuth().selectedBrand'den okunur — useBrand() bozuk,
 *    bkz. brand-context-source.
 * 🔴 Canlı çekim 10-25 sn sürüyor (backend'de 10 dk önbellek var). Yükleme
 *    durumu iskelet değil, açık bir mesajla gösteriliyor.
 * 🔴🔴 Rakip bazlı ses payı (Auction Insights) API'den GELMEZ; jeton kapalı.
 *    Sayfa bunu gizlemez, "Bilinen sınırlar" kutusunda yazar.
 */

const fmtInt = (n) => Number(Math.round(n || 0)).toLocaleString('tr-TR');
const fmtMoney = (n) => `₺${Number(Math.round(n || 0)).toLocaleString('tr-TR')}`;
const fmtMoney2 = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n, d = 1) => `%${(Number(n || 0) * 100).toFixed(d).replace('.', ',')}`;
const fmtNum = (n, d = 2) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

const TR_AY = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const label = (p) => {
  if (!p) return '—';
  const s = String(p);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`;
  return s;
};
const monthLabel = (p) => `${TR_AY[Number(String(p).slice(5, 7)) - 1]} ${String(p).slice(0, 4)}`;

const QUALITY_TR = {
  BELOW_AVERAGE: 'Ortalamanın altında',
  AVERAGE: 'Ortalama',
  ABOVE_AVERAGE: 'Ortalamanın üstünde',
};
const QUALITY_CLS = {
  BELOW_AVERAGE: 'bg-red-50 text-red-700 border-red-200',
  AVERAGE: 'bg-amber-50 text-amber-700 border-amber-200',
  ABOVE_AVERAGE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

// Google enum'ları olduğu gibi göstermek panelde yabancı duruyor.
const BID_TR = {
  TARGET_ROAS: 'Hedef ROAS', TARGET_CPA: 'Hedef EBM',
  MAXIMIZE_CONVERSIONS: 'Dönüşümleri en üst düzeye çıkar',
  MAXIMIZE_CONVERSION_VALUE: 'Dönüşüm değerini en üst düzeye çıkar',
  MAXIMIZE_CLICKS: 'Tıklamaları en üst düzeye çıkar',
  MANUAL_CPC: 'Manuel TBM', TARGET_IMPRESSION_SHARE: 'Hedef gösterim payı',
  TARGET_SPEND: 'Hedef harcama', PERCENT_CPC: 'Yüzde TBM',
};
const STATUS_TR = { ENABLED: 'Yayında', PAUSED: 'Duraklatıldı', REMOVED: 'Kaldırıldı' };
const STRENGTH_TR = {
  EXCELLENT: 'Mükemmel', GOOD: 'İyi', AVERAGE: 'Ortalama',
  POOR: 'Zayıf', PENDING: 'Bekliyor', NO_ADS: 'Reklam yok',
};
const MATCH_TR = { EXACT: 'Tam', PHRASE: 'Sıralı', BROAD: 'Geniş' };

const TABS = [
  { key: 'trend', label: 'Dönem tablosu' },
  { key: 'matrix', label: 'Kampanya × dönem' },
  { key: 'keywords', label: 'Kelimeler & kalite' },
  { key: 'terms', label: 'Arama terimleri' },
  { key: 'bench', label: 'Kampanya kıyası' },
  { key: 'scenarios', label: 'Senaryolar' },
  { key: 'structure', label: 'Yapı denetimi' },
];

function StatCard({ icon: Icon, tone, label: lbl, value, sub, warn }) {
  return (
    <div className={`rounded-xl border bg-white p-3 ${warn ? 'border-red-200' : 'border-gray-200'}`}>
      <div className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-lg grid place-items-center ${tone}`}><Icon className="w-4 h-4" /></span>
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{lbl}</span>
      </div>
      <div className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-gray-400 leading-tight mt-0.5">{sub}</div>}
    </div>
  );
}

/** Gösterim payı, bütçe kaybı ve sıralama kaybı toplamda %100 eder: tek çubuk. */
function ShareBar({ row }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-2.5 w-[120px] rounded-full overflow-hidden bg-gray-200">
        <div className="bg-gray-900" style={{ width: `${row.is * 100}%` }} title={`Aldığımız ${fmtPct(row.is)}`} />
        <div className="bg-amber-400" style={{ width: `${row.budgetLost * 100}%` }} title={`Bütçe kaybı ${fmtPct(row.budgetLost)}`} />
        <div className="bg-rose-500" style={{ width: `${row.rankLost * 100}%` }} title={`Sıralama kaybı ${fmtPct(row.rankLost)}`} />
      </div>
      <span className="text-xs tabular-nums text-gray-500 w-12 text-right">{fmtPct(row.is)}</span>
    </div>
  );
}

/**
 * Kampanya × dönem matrisi.
 * Hücre bir dönemin ham Google değeri; satır/sütun toplamları backend'de
 * havuza göre ağırlıklı hesaplanır (düz ortalama payı yanıltır).
 */
const MATRIX_METRICS = [
  { key: 'is', label: 'Gösterim payı', kind: 'share' },
  { key: 'budgetLost', label: 'Bütçe kaybı', kind: 'loss' },
  { key: 'rankLost', label: 'Sıralama kaybı', kind: 'loss' },
  { key: 'absTop', label: 'Mutlak üst pay', kind: 'share' },
  { key: 'impressions', label: 'Gösterim', kind: 'count' },
];
const weekLabel = (p) => `${String(p).slice(8, 10)}.${String(p).slice(5, 7)}`;
const shareTone = (v) => (v >= 0.8 ? 'bg-emerald-50 text-emerald-800'
  : v >= 0.6 ? 'bg-lime-50 text-lime-800'
  : v >= 0.4 ? 'bg-amber-50 text-amber-800'
  : 'bg-rose-50 text-rose-700');
const lossTone = (v) => (v >= 0.4 ? 'bg-rose-50 text-rose-700'
  : v >= 0.25 ? 'bg-amber-50 text-amber-800'
  : v >= 0.1 ? 'bg-gray-50 text-gray-600'
  : 'bg-white text-gray-400');

function MatrixCell({ cell, metric }) {
  // Boş hücre: kampanya o dönemde yayında değil. %0 DEĞİL, veri yok.
  if (!cell) return <td className="px-2 py-1.5 text-center text-gray-300">·</td>;
  const kind = MATRIX_METRICS.find((m) => m.key === metric)?.kind || 'share';
  const v = cell[metric] || 0;
  const cls = kind === 'count' ? 'text-gray-700' : kind === 'loss' ? lossTone(v) : shareTone(v);
  const tip = [
    `Gösterim payı ${fmtPct(cell.is)}`,
    `bütçe kaybı ${fmtPct(cell.budgetLost)}`,
    `sıralama kaybı ${fmtPct(cell.rankLost)}`,
    `gösterim ${fmtInt(cell.impressions)} / havuz ${fmtInt(cell.pool)}`,
    `maliyet ${fmtMoney(cell.cost)}`,
  ].join(' · ');
  return (
    <td className={`px-2 py-1.5 text-center tabular-nums whitespace-nowrap ${cls}`} title={tip}>
      {kind === 'count' ? fmtInt(v) : fmtPct(v)}
      {metric === 'is' && cell.bucketed && (
        <span className="text-[10px] text-gray-400 align-super" title="Google bu değeri kova olarak veriyor: '%10'dan az' / '%90'dan fazla'.">*</span>
      )}
    </td>
  );
}

/** Google'ın "%10'dan az" / "%90'dan fazla" kovası. Ölçüm değil, sınır. */
function Bucket() {
  return (
    <span className="ml-1 align-middle text-[10px] px-1 py-0.5 rounded border border-gray-300 text-gray-400" title="Google bu değeri '%10'dan az' / '%90'dan fazla' kovası olarak veriyor. Gerçek değer bilinmiyor.">
      kova
    </span>
  );
}

export default function SearchVisibility() {
  const { selectedBrand } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('trend');
  const [grain, setGrain] = useState('weekly');
  const [horizon, setHorizon] = useState('sprint14');
  const [matrix, setMatrix] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [mGrain, setMGrain] = useState('monthly');
  const [mMetric, setMMetric] = useState('is');

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'X-Brand-ID': selectedBrand?.id || '',
  }), [selectedBrand?.id]);

  // 1) Hesabın arama kampanyaları. En çok harcayan varsayılan seçilir.
  useEffect(() => {
    if (!selectedBrand?.id) return;
    let alive = true;
    (async () => {
      setListLoading(true); setError('');
      try {
        const r = await fetch(`${API_BASE}/api/ads-visibility/campaigns`, { credentials: 'include', headers });
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
        if (!alive) return;
        const list = j.campaigns || [];
        setCampaigns(list);
        setCampaignId((prev) => {
          if (prev) return prev;
          // Kullanıcının son seçimi markaya göre hatırlanır.
          const saved = localStorage.getItem(`adsVisibility:campaign:${selectedBrand.id}`);
          if (saved && list.some((c) => c.id === saved)) return saved;
          // Varsayılan EN ÇOK HARCAYAN değil, EN ÇOK GÖSTERİM KAÇIRAN kampanya:
          // bu sayfanın konusu görünürlük, en büyük fırsat orada duruyor.
          const worst = [...list].sort((a, b) => (b.pool - b.impressions) - (a.pool - a.impressions))[0];
          return worst?.id || list[0]?.id || '';
        });
      } catch (e) {
        if (alive) setError(e.message || 'Kampanyalar yüklenemedi');
      } finally {
        if (alive) setListLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedBrand?.id, headers]);

  // 2) Seçili kampanyanın tam dökümü
  const load = async () => {
    if (!campaignId || !selectedBrand?.id) return;
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_BASE}/api/ads-visibility/detail?campaignId=${campaignId}`, { credentials: 'include', headers });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setData(j);
    } catch (e) {
      setData(null);
      setError(e.message || 'Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (campaignId && selectedBrand?.id) {
      try { localStorage.setItem(`adsVisibility:campaign:${selectedBrand.id}`, campaignId); } catch (_) { /* özel pencerede yazamayabilir */ }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [campaignId, selectedBrand?.id]);

  // 3) Kampanya × dönem matrisi — sekmeye ilk girişte çekilir, kampanya
  //    seçiminden bağımsız (hesabın tamamı). Marka değişince sıfırlanır.
  useEffect(() => { setMatrix(null); }, [selectedBrand?.id]);

  useEffect(() => {
    if (tab !== 'matrix' || !selectedBrand?.id || matrix || matrixLoading) return;
    let alive = true;
    (async () => {
      setMatrixLoading(true); setError('');
      try {
        const r = await fetch(`${API_BASE}/api/ads-visibility/matrix`, { credentials: 'include', headers });
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
        if (alive) setMatrix(j);
      } catch (e) {
        if (alive) setError(e.message || 'Matris yüklenemedi');
      } finally {
        if (alive) setMatrixLoading(false);
      }
    })();
    return () => { alive = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tab, selectedBrand?.id]);

  const mx = matrix?.[mGrain] || null;
  const mLabel = (p) => (mGrain === 'monthly' ? monthLabel(p) : weekLabel(p));

  const downloadMatrixCsv = () => {
    if (!mx) return;
    const head = ['Kampanya', 'Durum', ...mx.periods.map(mLabel), 'Toplam'];
    const val = (cell) => (cell == null ? '' : mMetric === 'impressions'
      ? Math.round(cell.impressions)
      : String((cell[mMetric] || 0).toFixed(4)).replace('.', ','));
    const lines = [head, ...mx.campaigns.map((c) => [
      c.name, c.status, ...mx.periods.map((p) => val(c.cells[p])), val(c.total),
    ]), ['TOPLAM', '', ...mx.periods.map((p) => val(mx.periodTotals[p])), val(mx.total)]];
    const csv = '\uFEFF' + lines.map((r) => r.map((x) => `"${String(x ?? '').replace(/"/g, '""')}"`).join(';')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `gosterim-payi-${mGrain === 'monthly' ? 'aylik' : 'haftalik'}-${mMetric}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const s = data?.summary?.last30;
  const s14 = data?.summary?.last14;
  const p14 = data?.summary?.prev14;
  const lostTotal = s ? s.lostBudgetImpressions + s.lostRankImpressions : 0;

  const trendRows = data ? (data[grain] || []) : [];
  const scenarios = data?.scenarios?.[horizon] || [];
  const days = horizon === 'sprint14' ? 14 : 30;

  const keywords = useMemo(
    () => [...(data?.keywords || [])].sort((a, b) => b.impressions - a.impressions),
    [data]
  );
  const terms = useMemo(
    () => [...(data?.terms || [])].sort((a, b) => b.impressions - a.impressions),
    [data]
  );
  const bench = useMemo(
    () => [...(data?.campaigns || [])].filter((c) => c.cost >= 500).sort((a, b) => b.is - a.is),
    [data]
  );

  const delta = (a, b) => (b ? a / b - 1 : 0);
  const DeltaCell = ({ a, b, invert, neutral }) => {
    const d = delta(a, b);
    const good = invert ? d < 0 : d > 0;
    const cls = neutral ? 'text-gray-600' : Math.abs(d) < 0.005 ? 'text-gray-400' : good ? 'text-emerald-600' : 'text-red-600';
    return (
      <span className={`tabular-nums ${cls}`}>
        {d > 0 ? '+' : ''}{fmtPct(d)}
      </span>
    );
  };

  return (
    <div className="p-4 space-y-4">
      {/* ---- Başlık ---- */}
      <Card className="rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-4 flex flex-wrap items-center gap-3 justify-between border-b border-gray-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900">Arama Görünürlüğü &amp; Ses Payı</h1>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Canlı</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Google Ads&apos;ten anlık çekiliyor, hiçbir şey veritabanına yazılmıyor.
              {data?.settings?.name && <> Kampanya <b className="text-gray-700">{data.settings.name}</b>.</>}
              {data?.fetchedAt && <> Son çekim {new Date(data.fetchedAt).toLocaleString('tr-TR')}.</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              disabled={listLoading || !campaigns.length}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white max-w-[280px]"
            >
              {[...campaigns]
                .sort((a, b) => (b.pool - b.impressions) - (a.pool - a.impressions))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {fmtPct(c.is, 0)} · {fmtInt(c.pool - c.impressions)} kaçan
                  </option>
                ))}
            </select>
            <a
              href="/r/asics-gorunurluk"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
              title="Aynı analizin 20 slaytlık sunum hâli"
            >
              <Presentation className="w-4 h-4" /> Sunum
            </a>
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
            </button>
          </div>
        </div>

        {error && (
          <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
        )}
        {loading && (
          <div className="m-4 p-3 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-700">
            Google Ads&apos;ten canlı çekiliyor. Gösterim payı raporu 10-25 saniye sürüyor.
          </div>
        )}

        {/* ---- Ana ölçümler ---- */}
        {s && (
          <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard
              icon={Eye} tone="bg-gray-900 text-white" label="Gösterim payı"
              value={<>{fmtPct(s.is)}{s.bucketed && <Bucket />}</>}
              sub={`Son 30 gün · havuz ${fmtInt(s.pool)} gösterim`}
            />
            <StatCard
              icon={Wallet} tone="bg-amber-100 text-amber-600" label="Bütçe kaynaklı kayıp"
              value={fmtPct(s.budgetLost)}
              sub={`${fmtInt(s.lostBudgetImpressions)} gösterim · bütçeyle geri alınır`}
            />
            <StatCard
              icon={TrendingDown} tone="bg-rose-100 text-rose-600" label="Sıralama kaynaklı kayıp"
              value={fmtPct(s.rankLost)}
              sub={`${fmtInt(s.lostRankImpressions)} gösterim · teklif ve kalite ister`}
              warn={s.rankLost >= 0.4}
            />
            <StatCard
              icon={Crown} tone="bg-indigo-100 text-indigo-600" label="Mutlak üst konum payı"
              value={<>{s.absTop >= 0.0999 && s.absTop <= 0.09991 ? '<%10' : fmtPct(s.absTop)}</>}
              sub="Organik sonuçların üstündeki ilk reklam olduğumuz oran"
            />
            <StatCard
              icon={AlertTriangle} tone="bg-red-100 text-red-600" label="Kaçan gösterim"
              value={fmtInt(lostTotal)}
              sub={`≈ ${fmtInt(lostTotal * s.ctr)} tıklama · ≈ ${fmtMoney(lostTotal * s.ctr * s.rpc)} ciro`}
            />
          </div>
        )}

        {/* ---- Dönem karşılaştırması ---- */}
        {s && (
          <div className="px-4 pb-4">
            <div className="rounded-xl border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Metrik</th>
                    <th className="px-3 py-2.5 text-right">Son 7 gün</th>
                    <th className="px-3 py-2.5 text-right">Son 14 gün</th>
                    <th className="px-3 py-2.5 text-right">Önceki 14 gün</th>
                    <th className="px-3 py-2.5 text-right">Değişim</th>
                    <th className="px-3 py-2.5 text-right">Son 30 gün</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['Gösterim payı', (x) => fmtPct(x.is), 'is', false],
                    ['Bütçe kaynaklı kayıp', (x) => fmtPct(x.budgetLost), 'budgetLost', true],
                    ['Sıralama kaynaklı kayıp', (x) => fmtPct(x.rankLost), 'rankLost', true],
                    ['Uygun gösterim havuzu', (x) => fmtInt(x.pool), 'pool', false],
                    ['Gösterim', (x) => fmtInt(x.impressions), 'impressions', false],
                    ['Tıklama', (x) => fmtInt(x.clicks), 'clicks', false],
                    ['Maliyet', (x) => fmtMoney(x.cost), 'cost', false, true],
                    ['Ortalama TBM', (x) => fmtMoney2(x.cpc), 'cpc', true],
                    ['Ciro (Google Ads)', (x) => fmtMoney(x.value), 'value', false],
                    ['ROAS', (x) => fmtNum(x.roas), 'roas', false],
                  ].map(([name, fmt, key, invert, neutral]) => (
                    <tr key={name} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-700">{name}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmt(data.summary.last7)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{fmt(s14)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmt(p14)}</td>
                      <td className="px-3 py-2 text-right"><DeltaCell a={s14[key]} b={p14[key]} invert={invert} neutral={neutral} /></td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmt(s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              Oranlar basit ortalama değil, havuza göre ağırlıklı hesaplanır (Σgösterim ÷ Σhavuz).
              Gösterim payı, bütçe kaybı ve sıralama kaybı toplamda %100 eder.
            </p>
          </div>
        )}
      </Card>

      {/* ---- Sekmeler ---- */}
      {(data || matrix || matrixLoading) && (
        <Card className="rounded-2xl border border-gray-200 overflow-hidden">
          <div className="flex flex-wrap gap-1 px-3 pt-3 border-b border-gray-100">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-2 text-sm rounded-t-lg border-b-2 -mb-px transition ${
                  tab === t.key ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* -- Dönem tablosu -- */}
          {tab === 'trend' && data && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                {[['monthly', 'Aylık'], ['weekly', 'Haftalık'], ['daily', 'Günlük']].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setGrain(k)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${grain === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >{l}</button>
                ))}
                <span className="text-xs text-gray-400 ml-1">
                  {data.range.start} → {data.range.end}
                </span>
              </div>
              <div className="rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2.5 text-left">Dönem</th>
                      <th className="px-3 py-2.5 text-left w-[190px]">Pay dağılımı</th>
                      <th className="px-3 py-2.5 text-right">Bütçe kaybı</th>
                      <th className="px-3 py-2.5 text-right">Sıralama kaybı</th>
                      <th className="px-3 py-2.5 text-right">Havuz</th>
                      <th className="px-3 py-2.5 text-right">Gösterim</th>
                      <th className="px-3 py-2.5 text-right">Kaçan</th>
                      <th className="px-3 py-2.5 text-right">Tıklama</th>
                      <th className="px-3 py-2.5 text-right">Maliyet</th>
                      <th className="px-3 py-2.5 text-right">Ciro</th>
                      <th className="px-3 py-2.5 text-right">ROAS</th>
                      <th className="px-3 py-2.5 text-right">Mutlak üst</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[...trendRows].reverse().map((r) => (
                      <tr key={r.period} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                          {grain === 'monthly' ? monthLabel(r.period) : label(r.period)}
                          {r.bucketed && <Bucket />}
                        </td>
                        <td className="px-3 py-2"><ShareBar row={r} /></td>
                        <td className={`px-3 py-2 text-right tabular-nums ${r.budgetLost >= 0.25 ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>{fmtPct(r.budgetLost)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${r.rankLost >= 0.4 ? 'text-rose-700 font-semibold' : 'text-gray-500'}`}>{fmtPct(r.rankLost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtInt(r.pool)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtInt(r.impressions)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-600">{fmtInt(r.pool - r.impressions)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtInt(r.clicks)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtMoney(r.cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(r.value)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtNum(r.roas)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-400">
                          {r.absTopBucketed ? '<%10' : fmtPct(r.absTop)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Çubuk: koyu = aldığımız pay, turuncu = bütçe kaybı, kırmızı = sıralama kaybı.
                &quot;Kova&quot; etiketli satırlarda Google gerçek değeri vermiyor; havuz ve kaçan gösterim alt sınırdır.
              </p>
            </div>
          )}

          {/* -- Kelimeler -- */}
          {/* -- Kampanya × dönem matrisi -- */}
          {tab === 'matrix' && (
            <div className="p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {[['monthly', 'Aylık'], ['weekly', 'Haftalık']].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setMGrain(k)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${mGrain === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >{l}</button>
                ))}
                <span className="w-px h-6 bg-gray-200 mx-1" />
                {MATRIX_METRICS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setMMetric(m.key)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs border ${mMetric === m.key ? 'bg-blue-50 text-blue-700 border-blue-200 font-semibold' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                  >{m.label}</button>
                ))}
                {matrix && (
                  <>
                    <span className="text-xs text-gray-400 ml-1">{matrix.range.start} → {matrix.range.end}</span>
                    <button
                      onClick={downloadMatrixCsv}
                      className="ml-auto px-3 py-1.5 rounded-lg text-xs border border-gray-200 text-gray-600 hover:bg-gray-50"
                    >CSV indir</button>
                  </>
                )}
              </div>

              {matrixLoading && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  Hesabın tüm arama kampanyaları Google Ads'ten çekiliyor — 10-25 saniye sürebilir.
                </div>
              )}

              {!matrixLoading && mx && !mx.campaigns.length && (
                <div className="rounded-xl border border-gray-200 px-4 py-8 text-center text-sm text-gray-400">
                  Bu pencerede gösterim almış arama kampanyası yok.
                </div>
              )}

              {!matrixLoading && !!mx?.campaigns.length && (
                <div className="rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                      <tr>
                        <th className="px-3 py-2.5 text-left sticky left-0 bg-gray-50 z-10 min-w-[240px]">Kampanya</th>
                        {mx.periods.map((pr) => (
                          <th key={pr} className="px-2 py-2.5 text-center whitespace-nowrap">{mLabel(pr)}</th>
                        ))}
                        <th className="px-3 py-2.5 text-center border-l border-gray-200">Toplam</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {mx.campaigns.map((c) => (
                        <tr key={c.id} className="hover:bg-gray-50/70">
                          <td className="px-3 py-1.5 sticky left-0 bg-white z-10">
                            <button
                              className="font-medium text-gray-800 hover:text-blue-700 text-left"
                              title="Bu kampanyanın tam dökümüne geç"
                              onClick={() => { setCampaignId(c.id); setTab('trend'); }}
                            >
                              {c.name}
                            </button>
                            <div className="text-[10px] text-gray-400">
                              {c.status !== 'ENABLED' && <span className="mr-1 text-amber-600">{c.status}</span>}
                              {fmtMoney(c.total.cost)} · {fmtInt(c.total.impressions)} gösterim
                            </div>
                          </td>
                          {mx.periods.map((pr) => <MatrixCell key={pr} cell={c.cells[pr]} metric={mMetric} />)}
                          <td className="px-3 py-1.5 text-center tabular-nums font-semibold text-gray-800 border-l border-gray-200">
                            {mMetric === 'impressions' ? fmtInt(c.total.impressions) : fmtPct(c.total[mMetric])}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200 font-semibold text-gray-700">
                      <tr>
                        <td className="px-3 py-2 sticky left-0 bg-gray-50 z-10">
                          Hesap toplamı <span className="font-normal text-gray-400">· {mx.campaigns.length} kampanya</span>
                        </td>
                        {mx.periods.map((pr) => (
                          <td key={pr} className="px-2 py-2 text-center tabular-nums">
                            {mMetric === 'impressions'
                              ? fmtInt(mx.periodTotals[pr]?.impressions)
                              : fmtPct(mx.periodTotals[pr]?.[mMetric])}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-center tabular-nums border-l border-gray-200">
                          {mMetric === 'impressions' ? fmtInt(mx.total.impressions) : fmtPct(mx.total[mMetric])}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <p className="text-[11px] text-gray-400 mt-2">
                Hücre Google'ın o dönem için verdiği ham değer; üstüne gelince gösterim, havuz ve kayıp kırılımı çıkar.
                Boş hücre (·) o dönemde kampanyanın yayında olmadığını söyler, %0 demek değildir.
                <span className="align-super">*</span> işaretli hücrede Google gerçek sayıyı vermiyor, &quot;%10&apos;dan az&quot; / &quot;%90&apos;dan fazla&quot; kovası.
                Satır ve sütun toplamları dönemlerin düz ortalaması değil, uygun gösterim havuzuna göre ağırlıklı.
                Haftalıkta ilk ve son sütun pencerenin kenarına denk geldiği için eksik gün içerir; sütun başlığı haftanın Pazartesi'sidir.
              </p>
            </div>
          )}

          {tab === 'keywords' && data && (
            <div className="p-4">
              <div className="rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2.5 text-left">Kelime</th>
                      <th className="px-3 py-2.5 text-left">Eşleşme</th>
                      <th className="px-3 py-2.5 text-right">Gösterim</th>
                      <th className="px-3 py-2.5 text-right">Tıklama</th>
                      <th className="px-3 py-2.5 text-right">Maliyet</th>
                      <th className="px-3 py-2.5 text-right">Ciro</th>
                      <th className="px-3 py-2.5 text-right">Gösterim payı</th>
                      <th className="px-3 py-2.5 text-right">Mutlak üst</th>
                      <th className="px-3 py-2.5 text-center">KP</th>
                      <th className="px-3 py-2.5 text-left">Beklenen TO</th>
                      <th className="px-3 py-2.5 text-left">Reklam alaka</th>
                      <th className="px-3 py-2.5 text-left">Açılış sayfası</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {keywords.map((k) => (
                      <tr key={`${k.adGroup}|${k.text}|${k.matchType}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-800">{k.text}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{MATCH_TR[k.matchType] || k.matchType}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtInt(k.impressions)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtInt(k.clicks)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(k.cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(k.value)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{fmtPct(k.is)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-400">
                          {Math.abs(k.absTop - 0.0999) < 1e-6 ? '<%10' : fmtPct(k.absTop)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {k.qualityScore ? (
                            <span className={`inline-block w-7 py-0.5 rounded-md text-xs font-bold text-white ${
                              k.qualityScore <= 4 ? 'bg-rose-500' : k.qualityScore <= 6 ? 'bg-amber-500' : 'bg-emerald-600'
                            }`}>{k.qualityScore}</span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        {['expectedCtr', 'adRelevance', 'landingPage'].map((f) => (
                          <td key={f} className="px-3 py-2">
                            {k[f] ? (
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${QUALITY_CLS[k[f]] || 'border-gray-200 text-gray-500'}`}>
                                {QUALITY_TR[k[f]] || k[f]}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Performans penceresi {data.range.perfStart} → {data.range.end}. Kalite bilgisi anlık durumdur, tarih aralığından bağımsızdır.
                Kelime seviyesinde bütçe ve sıralama kaybı Google tarafından raporlanmaz; o kırılım yalnızca kampanya seviyesinde vardır.
              </p>
            </div>
          )}

          {/* -- Arama terimleri -- */}
          {tab === 'terms' && data && (
            <div className="p-4">
              <div className="rounded-xl border border-gray-200 overflow-x-auto max-h-[560px]">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0">
                    <tr>
                      <th className="px-3 py-2.5 text-left">Arama sorgusu</th>
                      <th className="px-3 py-2.5 text-right">Gösterim</th>
                      <th className="px-3 py-2.5 text-right">Tıklama</th>
                      <th className="px-3 py-2.5 text-right">TO</th>
                      <th className="px-3 py-2.5 text-right">Maliyet</th>
                      <th className="px-3 py-2.5 text-right">TBM</th>
                      <th className="px-3 py-2.5 text-right">Dönüşüm</th>
                      <th className="px-3 py-2.5 text-right">Ciro</th>
                      <th className="px-3 py-2.5 text-right">ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {terms.map((t) => (
                      <tr key={t.term} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-800">{t.term}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtInt(t.impressions)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtInt(t.clicks)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtPct(t.impressions ? t.clicks / t.impressions : 0)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(t.cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney2(t.clicks ? t.cost / t.clicks : 0)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtNum(t.conversions, 1)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(t.value)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{t.cost ? fmtNum(t.value / t.cost) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">Gösterime göre ilk 100 sorgu. Arama terimi raporunda gösterim payı yoktur.</p>
            </div>
          )}

          {/* -- Kampanya kıyası -- */}
          {tab === 'bench' && data && (
            <div className="p-4">
              <div className="rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2.5 text-left">Kampanya</th>
                      <th className="px-3 py-2.5 text-left w-[190px]">Gösterim payı</th>
                      <th className="px-3 py-2.5 text-right">Gösterim</th>
                      <th className="px-3 py-2.5 text-right">Tıklama</th>
                      <th className="px-3 py-2.5 text-right">Maliyet</th>
                      <th className="px-3 py-2.5 text-right">Ciro</th>
                      <th className="px-3 py-2.5 text-right">ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {bench.map((c) => {
                      const own = c.id === campaignId;
                      return (
                        <tr key={c.id} className={own ? 'bg-blue-50/60' : 'hover:bg-gray-50'}>
                          <td className={`px-3 py-2 ${own ? 'font-semibold text-blue-900' : 'text-gray-800'}`}>{c.name}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="h-2.5 w-[120px] rounded-full bg-gray-200 overflow-hidden">
                                <div className={own ? 'h-full bg-blue-600' : 'h-full bg-gray-400'} style={{ width: `${Math.min(100, c.is * 100)}%` }} />
                              </div>
                              <span className="text-xs tabular-nums text-gray-600 w-12 text-right">{fmtPct(c.is, 0)}</span>
                              {c.bucketed && <Bucket />}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtInt(c.impressions)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtInt(c.clicks)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtMoney(c.cost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(c.value)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{c.cost ? fmtNum(c.value / c.cost) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">
                Son 30 gün, hesabın arama kampanyaları. Harcaması 500 ₺ altındakiler gizlendi.
                Seçili kampanya mavi işaretli.
              </p>
            </div>
          )}

          {/* -- Senaryolar -- */}
          {tab === 'scenarios' && data && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                {[['sprint14', '2 haftalık sprint'], ['month30', '30 günlük plan']].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setHorizon(k)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${horizon === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >{l}</button>
                ))}
                <span className="text-xs text-gray-400 ml-1">
                  Taban: son {days} günün gerçekleşen TO, TBM ve tıklama başı cirosu
                </span>
              </div>
              <div className="rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2.5 text-left">Senaryo</th>
                      <th className="px-3 py-2.5 text-right">Gösterim payı</th>
                      <th className="px-3 py-2.5 text-right">Günlük bütçe</th>
                      <th className="px-3 py-2.5 text-right">{days} günlük maliyet</th>
                      <th className="px-3 py-2.5 text-right">Ek maliyet</th>
                      <th className="px-3 py-2.5 text-right">Ek gösterim</th>
                      <th className="px-3 py-2.5 text-right">Ek tıklama</th>
                      <th className="px-3 py-2.5 text-right">Tahmini ciro</th>
                      <th className="px-3 py-2.5 text-right">ROAS</th>
                      <th className="px-3 py-2.5 text-right">Marjinal ROAS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {scenarios.map((sc, i) => (
                      <tr key={sc.name} className={i === 3 ? 'bg-emerald-50/60' : 'hover:bg-gray-50'}>
                        <td className={`px-3 py-2 ${i === 3 ? 'font-semibold text-emerald-900' : 'text-gray-800'}`}>
                          {sc.name}
                          {i === 3 && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-600 text-white">önerimiz</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{fmtPct(sc.is, 0)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtMoney(sc.dailyBudget)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtMoney(sc.cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{i ? `+${fmtMoney(sc.newCost)}` : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{i ? `+${fmtInt(sc.newImpressions)}` : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{i ? `+${fmtInt(sc.newClicks)}` : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">{fmtMoney(sc.value)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmtNum(sc.roas)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700">{i ? fmtNum(sc.marginalRoas) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 p-3 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-600 space-y-1">
                <p><b>Model nasıl çalışıyor.</b> Bütçe kaynaklı kayıp her senaryoda tamamen geri alınır ve mevcut tıklama maliyetiyle hesaplanır: bunlar zaten kazandığımız açık artırmalar, sadece bütçe veya gün içi tempolama yüzünden gösterilmiyor.</p>
                <p>Sıralama kaynaklı kayıp yalnızca kısmen ve daha pahalı geri gelir. Modelde tıklama maliyeti S2&apos;de 1,15, S3&apos;te 1,30, S4&apos;te 1,50 katına çıkar; yeni trafiğin tıklama başı cirosu sırasıyla 0,95, 0,90 ve 0,85 ile çarpılır.</p>
                <p>Ayarlanacak günlük bütçe, hesaplanan günlük harcamanın %15 üstüdür. Tamamının harcanması beklenmez; amaç Google&apos;ın tempolamasına pay bırakmak.</p>
                <p className="text-red-600"><b>Bu bir tahmindir, taahhüt değildir.</b> Sıralama kaynaklı kaybın ne kadarının geri alınabileceği rakip tekliflerine bağlıdır ve ilk 72 saatte ölçülüp güncellenmelidir.</p>
              </div>
            </div>
          )}

          {/* -- Yapı denetimi -- */}
          {tab === 'structure' && data && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {[
                  ['Reklam grubu', data.structure.adGroupCount, Layers, data.structure.adGroupCount <= 1],
                  ['Kelime', keywords.length, Target, false],
                  ['Tam eşleşme', keywords.filter((k) => k.matchType === 'EXACT').length, Target, keywords.filter((k) => k.matchType === 'EXACT').length === 0],
                  ['Reklam', data.structure.adCount, Layers, data.structure.adCount <= 1],
                  ['Negatif kelime', data.structure.negativeCount, Target, data.structure.negativeCount < 20],
                  ['Günlük bütçe', fmtMoney(data.settings.dailyBudget), Wallet, false],
                ].map(([lbl, val, Icon, warn]) => (
                  <div key={lbl} className={`rounded-xl border p-3 ${warn ? 'border-rose-200 bg-rose-50/40' : 'border-gray-200 bg-white'}`}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${warn ? 'text-rose-500' : 'text-gray-400'}`} />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{lbl}</span>
                    </div>
                    <div className={`mt-1 text-2xl font-bold tabular-nums ${warn ? 'text-rose-600' : 'text-gray-900'}`}>{val}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-100">
                    {[
                      ['Teklif stratejisi', BID_TR[data.settings.biddingStrategy] || data.settings.biddingStrategy],
                      ['Kampanya durumu', STATUS_TR[data.settings.status] || data.settings.status],
                      ['Başlangıç', data.settings.startDate],
                      ['Optimizasyon puanı', fmtPct(data.settings.optimizationScore, 0)],
                      ['Paylaşımlı bütçe', data.settings.sharedBudget ? 'Evet' : 'Hayır'],
                      ['Reklam gücü', (data.structure.adStrengths || []).map((x) => STRENGTH_TR[x] || x).join(', ') || '—'],
                      ['Kalite puanı medyanı', (() => {
                        const q = keywords.map((k) => k.qualityScore).filter(Boolean).sort((a, b) => a - b);
                        return q.length ? q[Math.floor(q.length / 2)] : '—';
                      })()],
                      ['Beklenen TO "ortalamanın altında"', `${keywords.filter((k) => k.expectedCtr === 'BELOW_AVERAGE').length} kelime`],
                      ['Açılış sayfası "ortalamanın altında"', `${keywords.filter((k) => k.landingPage === 'BELOW_AVERAGE').length} kelime`],
                      ['Kampanya varlıkları', Object.entries(data.structure.assets || {}).map(([k, v]) => `${k} ${v}`).join(' · ') || 'yok'],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <td className="px-3 py-2 text-gray-500 w-1/3">{k}</td>
                        <td className="px-3 py-2 text-gray-900 font-medium">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400">
                Kırmızı kutular eşik altındadır: tek reklam grubu, hiç tam eşleşme olmaması, tek reklam ve 20&apos;den az negatif kelime.
                Bunların her biri Ad Rank üzerinden sıralama kaynaklı kaybı doğrudan büyütür.
              </p>
            </div>
          )}
        </Card>
      )}

      {/* ---- Bilinen sınırlar ---- */}
      {data?.limits?.length > 0 && (
        <Card className="rounded-2xl border border-amber-200 bg-amber-50/50 overflow-hidden">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-amber-600" />
              <h2 className="text-sm font-semibold text-amber-900">Bilinen sınırlar</h2>
            </div>
            <ul className="space-y-1.5">
              {data.limits.map((l) => (
                <li key={l} className="text-xs text-amber-900/80 leading-relaxed pl-4 relative">
                  <span className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full bg-amber-400" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}
    </div>
  );
}
